import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ar from "@/locales/ar.json";
import en from "@/locales/en.json";

const dictionaries = { ar, en };

const I18nContext = createContext(null);

function getLangFromPath(pathname) {
  if (pathname.startsWith("/en")) return "en";
  if (pathname.startsWith("/ar")) return "ar";
  return "ar";
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => getLangFromPath(location.pathname));

  useEffect(() => {
    const next = getLangFromPath(location.pathname);
    if (next !== lang) setLang(next);
    document.documentElement.lang = next === "en" ? "en" : "es-AR";
  }, [location.pathname, lang]);

  const t = useCallback(
    (path, fallback = "") => {
      const dict = dictionaries[lang] || dictionaries.ar;
      const parts = path.split(".");
      let cur = dict;
      for (const p of parts) {
        if (cur && Object.prototype.hasOwnProperty.call(cur, p)) cur = cur[p];
        else return fallback || path;
      }
      return cur === undefined || cur === null ? fallback || path : cur;
    },
    [lang]
  );

  const switchLang = useCallback(
    (next) => {
      if (next === lang) return;
      const path = location.pathname;
      let newPath = path;
      if (path === "/thank_you") {
        // Thank you is Spanish only — do nothing
        return;
      }
      if (path.startsWith("/ar")) newPath = path.replace(/^\/ar/, `/${next}`);
      else if (path.startsWith("/en")) newPath = path.replace(/^\/en/, `/${next}`);
      else newPath = `/${next}`;
      // Preserve hash so anchor doesn't jump
      navigate(newPath + location.hash, { replace: false, state: { fromLangSwitch: true } });
      setLang(next);
    },
    [lang, location.pathname, location.hash, navigate]
  );

  const value = useMemo(() => ({ lang, t, switchLang }), [lang, t, switchLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
